import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

const ROOT_DOMAIN = import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN ?? 'storeo.com.br';

const PIX_KEY_TYPE_LABEL: Record<string, string> = {
  cpf: 'CPF',
  email: 'E-mail',
  phone: 'Telefone',
  random: 'Chave aleatória',
};

const FIELD =
  'w-full px-space-md py-space-sm rounded-lg bg-surface-container-lowest text-on-surface font-body-md text-body-md placeholder:text-outline focus:outline-none focus:bg-surface-container transition-all shadow-inner';

const TABS = ['Domínio & DNS', 'Faturamento & Repasse', 'Pixels & Rastreamento', 'Gateways', 'Equipe & Acessos'] as const;
type Tab = (typeof TABS)[number];

/** Configurações & Domínios — redesign Lumina (docs/prototype/storeo_configura_es_dom_nios).
 *  Dado real: kyc (chave PIX). DNS/pixels/gateways/equipe/API = mockup — sem backend. */
export function Config() {
  const [tab, setTab] = useState<Tab>('Domínio & DNS');
  const store = useQuery({ queryKey: ['store'], queryFn: () => api.store() });
  const host = store.data?.subdomain ? `${store.data.subdomain}.${ROOT_DOMAIN}` : ROOT_DOMAIN;

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      <div className="flex flex-col gap-space-3xs">
        <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold">Arquitetura de Operações</span>
        <h1 className="font-headline-lg text-headline-lg text-on-surface tracking-tight">Configurações &amp; Domínios</h1>
        <p className="font-body-md text-body-md text-on-surface-variant">Domínio, faturamento, rastreamento, gateways e acessos da equipe.</p>
      </div>

      <div className="flex flex-wrap items-center gap-space-xs rounded-2xl bg-surface-container-low p-space-2xs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-space-md py-space-xs rounded-xl font-label-md text-label-md transition-all ${
              tab === t ? 'bg-primary-container text-on-primary-container font-semibold' : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Domínio & DNS' ? <DomainTab host={host} /> : null}
      {tab === 'Faturamento & Repasse' ? <BillingTab /> : null}
      {tab !== 'Domínio & DNS' && tab !== 'Faturamento & Repasse' ? (
        <>
          <DemoBanner />
          <div className="rounded-2xl bg-surface-container-low p-space-2xl shadow-md flex flex-col items-center text-center gap-space-sm">
            <MSym name="construction" className="text-outline text-headline-lg" />
            <span className="font-headline-sm text-headline-sm text-on-surface font-bold">{tab}</span>
            <span className="font-body-md text-body-md text-on-surface-variant max-w-md">
              Tela desenhada, integração de backend em breve.
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

function DomainTab({ host }: { host: string }) {
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-md">
        <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
          <div className="flex items-center justify-between">
            <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">Subdomínio Storeo</span>
            <span className="px-space-xs py-space-3xs rounded-full bg-secondary-container/20 text-secondary font-label-sm text-label-sm font-semibold flex items-center gap-1">
              <MSym name="lock" className="text-[14px]" /> SSL Vitalício
            </span>
          </div>
          <span className="font-headline-md text-headline-md text-on-surface font-bold break-all">{host}</span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">Já no ar, com HTTPS automático. Nada a configurar.</span>
          <span className="flex items-center gap-space-3xs font-label-sm text-label-sm text-secondary mt-auto">
            <MSym name="check_circle" className="text-[16px]" /> Propagação global OK
          </span>
        </div>

        <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
          <div className="flex items-center justify-between">
            <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider flex items-center">
              Domínio próprio <PlaceholderTag />
            </span>
            <span className="px-space-xs py-space-3xs rounded-full bg-surface-container-high text-on-surface-variant font-label-sm text-label-sm">Cloudflare CNAME</span>
          </div>
          <span className="font-headline-md text-headline-md text-on-surface-variant font-bold">sualoja.com.br</span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">
            Conecte um .com.br ou .com — a Storeo emite o certificado TLS na borda automaticamente.
          </span>
          <button className="self-start px-space-md py-space-xs rounded-xl bg-primary/10 text-primary font-label-sm text-label-sm font-bold mt-auto">
            Conectar domínio
          </button>
        </div>
      </div>

      <DemoBanner>A tabela de apontamento DNS abaixo é ilustrativa.</DemoBanner>

      <div className="rounded-2xl bg-surface-container overflow-hidden shadow-lg">
        <div className="p-space-lg">
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Tabela de apontamento DNS</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse font-body-sm text-body-sm">
            <thead>
              <tr className="bg-surface-container-highest/40 text-on-surface font-label-sm text-label-sm uppercase tracking-wider font-semibold">
                <th className="py-space-md px-space-lg">Tipo</th>
                <th className="py-space-md px-space-md">Nome / Host</th>
                <th className="py-space-md px-space-md">Destino</th>
                <th className="py-space-md px-space-md">TTL</th>
                <th className="py-space-md px-space-lg">Status</th>
              </tr>
            </thead>
            <tbody className="text-on-surface font-mono">
              {[
                ['CNAME', 'www', 'cname.storeo-edge.com', 'Auto', 'Ativo & Validado'],
                ['A', '@ (root)', '76.76.21.21', 'Auto', 'Ativo & Validado'],
                ['TXT', 'storeo-verify', 'storeo-verify=…', 'Auto', 'Autenticado'],
              ].map((r) => (
                <tr key={r[1]} className="border-t border-surface-container-highest/30">
                  <td className="py-space-md px-space-lg">
                    <span className="px-2 py-0.5 rounded bg-surface-variant text-outline font-label-sm text-label-sm font-bold font-sans">{r[0]}</span>
                  </td>
                  <td className="py-space-md px-space-md text-on-surface-variant">{r[1]}</td>
                  <td className="py-space-md px-space-md text-on-surface-variant">{r[2]}</td>
                  <td className="py-space-md px-space-md text-outline">{r[3]}</td>
                  <td className="py-space-md px-space-lg">
                    <span className="font-sans px-2 py-0.5 rounded-full bg-secondary-container/20 text-secondary font-label-sm text-label-sm font-bold">
                      {r[4]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function BillingTab() {
  const qc = useQueryClient();
  const kyc = useQuery({ queryKey: ['kyc'], queryFn: () => api.kyc() });
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('email');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (kyc.data) {
      setPixKey(kyc.data.pixKey);
      setPixKeyType(kyc.data.pixKeyType);
    }
  }, [kyc.data]);

  const save = useMutation({
    mutationFn: () => api.updatePixKey({ pixKey, pixKeyType }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kyc'] });
      setSaved(true);
      setError(null);
    },
    onError: (e: unknown) => {
      setSaved(false);
      setError(e instanceof Error ? e.message : 'Não foi possível salvar a chave PIX');
    },
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-md">
      <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
        <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Comissão</h2>
        <p className="font-body-md text-body-md text-on-surface-variant">
          A Storeo fica com <b className="text-on-surface">7,99%</b> de cada venda — sem mensalidade, sem taxa fixa.
          Igual pra todo mundo, não é ajustável por loja.
        </p>
      </div>

      <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
        <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Chave PIX de repasse</h2>
        {kyc.isLoading ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">Carregando…</p>
        ) : kyc.isError ? (
          <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">
            Não foi possível carregar sua chave PIX.
          </p>
        ) : (
          <form
            className="flex flex-col gap-space-md"
            onSubmit={(e) => {
              e.preventDefault();
              setSaved(false);
              save.mutate();
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
              <label className="flex flex-col gap-space-2xs font-label-sm text-label-sm text-on-surface-variant font-medium">
                Tipo
                <select className={FIELD} value={pixKeyType} onChange={(e) => setPixKeyType(e.target.value)}>
                  {Object.entries(PIX_KEY_TYPE_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-space-2xs font-label-sm text-label-sm text-on-surface-variant font-medium">
                Chave
                <input className={FIELD} required value={pixKey} onChange={(e) => setPixKey(e.target.value)} />
              </label>
            </div>
            <span className="font-label-sm text-label-sm text-outline">É pra essa chave que o repasse cai — confira antes de salvar.</span>
            {error ? (
              <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">{error}</p>
            ) : null}
            {saved ? <p className="font-body-sm text-body-sm text-secondary">✓ Chave PIX atualizada.</p> : null}
            <button
              className="self-start px-space-lg py-space-xs rounded-xl bg-primary text-on-primary font-label-lg text-label-lg font-bold disabled:opacity-60"
              type="submit"
              disabled={save.isPending}
            >
              {save.isPending ? 'Salvando…' : 'Salvar chave PIX'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
